<template>
    <div
        class="flex text-sm rounded pl-1 pr-3 text-gray w-20 h-[26px] tp-root"
        :class="{ 'tp-invalid': !isValid }"
        :style="{ 'background-color': bgclr || '#f2f2f2' }"
    >
        <input ref="hoursInput" :disabled type="text" @input="onInputHours"  @blur="onBlurHours" @focus="selectInputText" maxlength="2" v-model="hours" class="w-7  text-right pr-[2px] border-none outline-none tp-input">
        <span class="relative top-[2px]">:</span>
        <input ref="minutesInput" :disabled @input="onInputMinutes" @keydown="onMinutesKeydown" @blur="onBlurMinutes" @focus="selectInputText" maxlength="2" v-model="minutes" type="text" class="w-7 pl-[2px] border-none tp-input outline-none">
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

const model = defineModel<string | null>()
const isValid = defineModel<boolean>('isValid', { default: true })

const { disabled = false, bgclr = '', required = false } = defineProps<{
    disabled?: boolean,
    bgclr?: string,
    required?: boolean
}>()

const hours = ref('')
const minutes = ref('')
const hoursInput = ref<HTMLInputElement | null>(null)
const minutesInput = ref<HTMLInputElement | null>(null)

const isEmpty = (value: string) => value === '' || value === '??'
const setIfChanged = (next: string | null) => {
    const normalizedCurrent = model.value ?? null
    if (normalizedCurrent !== next) model.value = next
}

const normalizeHour = (value: string): string | null => {
    if (!value) return null
    if (!/^\d{1,2}$/.test(value)) return null
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 23) return null
    return String(parsed).padStart(2, '0')
}

const normalizeMinute = (value: string): string | null => {
    if (!value) return null
    if (!/^\d{1,2}$/.test(value)) return null
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 59) return null
    return String(parsed).padStart(2, '0')
}

const setLocalFromModel = (value: string | null) => {
    if (value) {
        const nextHours = value.substring(0, 2)
        const nextMinutes = value.substring(3, 5)
        if (hours.value !== nextHours) hours.value = nextHours
        if (minutes.value !== nextMinutes) minutes.value = nextMinutes
    } else {
        if (hours.value !== '??') hours.value = '??'
        if (minutes.value !== '??') minutes.value = '??'
    }
}

const commitDraft = () => {
    const rawHours = isEmpty(hours.value) ? '' : hours.value
    const rawMinutes = isEmpty(minutes.value) ? '' : minutes.value
    const bothEmpty = !rawHours && !rawMinutes

    if (bothEmpty) {
        if (required) {
            isValid.value = false
            setIfChanged(null)
        } else {
            isValid.value = true
            setIfChanged(null)
        }
        return
    }

    if (!rawHours || !rawMinutes) {
        isValid.value = false
        setIfChanged(null)
        return
    }

    const normalizedHour = normalizeHour(rawHours)
    const normalizedMinute = normalizeMinute(rawMinutes)

    if (!normalizedHour || !normalizedMinute) {
        isValid.value = false
        setIfChanged(null)
        return
    }

    isValid.value = true
    setIfChanged(`${normalizedHour}:${normalizedMinute}`)
}

const normalizeOnBlur = () => {
    const rawHours = isEmpty(hours.value) ? '' : hours.value
    const rawMinutes = isEmpty(minutes.value) ? '' : minutes.value

    if (!rawHours && !rawMinutes) {
        hours.value = '??'
        minutes.value = '??'
        return
    }

    const normalizedHour = normalizeHour(rawHours)
    const normalizedMinute = normalizeMinute(rawMinutes)

    if (normalizedHour) hours.value = normalizedHour
    if (normalizedMinute) minutes.value = normalizedMinute
}

onMounted(() => {
    setLocalFromModel(model.value ?? null)
    commitDraft()
})

watch(model, (value) => {
    const localHour = isEmpty(hours.value) ? null : normalizeHour(hours.value)
    const localMinute = isEmpty(minutes.value) ? null : normalizeMinute(minutes.value)
    const normalizedLocal = (localHour && localMinute) ? `${localHour}:${localMinute}` : null

    const normalizedValue = value ?? null
    if (normalizedValue === normalizedLocal) return
    setLocalFromModel(normalizedValue)
    commitDraft()
})

const selectInputText = (event: FocusEvent) => {
    const target = event.target
    if (target instanceof HTMLInputElement) {
        target.select()
    }
}

const onInputHours = () => {
    hours.value = hours.value.replace(/\D/g, '').slice(0, 2)
    if (hours.value.length > 1) minutesInput.value?.focus()
    commitDraft()
}

const onBlurHours = () => {
    normalizeOnBlur()
    commitDraft()
}

const onInputMinutes = () => {
    minutes.value = minutes.value.replace(/\D/g, '').slice(0, 2)
    commitDraft()
}

const onMinutesKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Backspace') return
    if (minutes.value !== '') return

    event.preventDefault()
    hoursInput.value?.focus()
}

const onBlurMinutes = () => {
    normalizeOnBlur()
    commitDraft()
}

</script>

<style scoped>
.tp-input {
    background-color: transparent;
}

.tp-root {
    border: 1px solid transparent;
}

.tp-invalid {
    border-color: #fa7e48;
    background-color: #ffd5c3 !important;
}
</style>
